import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Paper,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { getUserDetails } from '../../storageService';
import { getMilestonefaculty, getMilestoneStudent } from '../../Utils/apiService';
import { postFacultyStudentProgressData } from '../../Utils/apiService';
import KenLoader from '../../GlobalComponents/KenLoader';

interface Student {
  id: string;
  name: string;
  status: any;
  comments: string;
  isManuallyUpdated?: boolean;
}

interface MilestoneData {
  title: string;
  currentValue?: number;
  studentsOnTrack: { done: number; total: number };
  completionPercentage: number;
  students: Student[];
}

// const statusToPercent = (status: string): number => {
//   if (!status) return 0;
//   switch (status.toLowerCase()) {
//     case 'not started':
//       return 0;
//     case 'in progress':
//       return 25;
//     case 'half done':
//     case '50% done':
//       return 50;
//     case 'almost done':
//     case '75% done':
//       return 75;
//     case 'completed':
//     case 'done':
//       return 100;
//     default:
//       return 0;
//   }
// };

const EditableTable: React.FC = () => {
  const { goalId } = useParams<{ goalId: string }>();
  const userType = (getUserDetails()?.Type as 'Faculty' | 'Student') || 'Student';
  const isFaculty = userType === 'Faculty';
  const [data, setData] = useState<MilestoneData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [students, setStudents] = useState<Student[]>([]);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [tempComments, setTempComments] = useState<string>('');
  const [showNoDataMessage, setShowNoDataMessage] = useState(false);

  useEffect(() => {
    if (!goalId) return;
    setLoading(true);
    setError(null);
    const timeout = setTimeout(() => setShowNoDataMessage(true), 5000);

    const fetchData = async () => {
      try {
        if (isFaculty) {
          const res: any = await getMilestonefaculty(goalId);

          const mapped = (res.data || []).map((item: any) => ({
            id: item.registrationNumber,
            name:  item.contactName || 'Unknown',
            status: item.status,
            comments: item.comments || '',
          }));

          const done = mapped.filter((s: Student) => s.status >= 50).length;

          const total = mapped.length;
          
          const firstItemWithProgress = (res.data || []).find((item: any) => item.progress);
          const progressValue = firstItemWithProgress?.progress ? parseInt(firstItemWithProgress.progress) : 0;
          
          setData({
            title: 'Faculty Milestone',
            studentsOnTrack: { done, total },
            completionPercentage: progressValue,
            students: mapped,
          });
          setStudents(mapped);
        } else {
          const res: any = await getMilestoneStudent(goalId);
          const goalData = res.data?.[0] || {};
          
          const mapped = (res.data || []).map((sg: any, idx: number) => ({
            id: sg.Id || `subgoal-${idx}`,
            name: sg.Name || 'Untitled',
            status: sg.Status || 'Not Started',
            comments: sg.Comments || '',
          }));
          
          const done = mapped.filter((s: Student) => s.status === 'Completed' || s.status === 'Done').length;
          const total = mapped.length;
          
          const apiProgress = parseInt(goalData.Progress || goalData.CurrentValue, 10);
          const percent = !isNaN(apiProgress)
            ? apiProgress
            : total
              ? Math.round((done / total) * 100)
              : 0;
          
          setData({
            title: goalData.Name || 'Student Milestone',
            studentsOnTrack: { done, total },
            completionPercentage: percent,
            currentValue: percent,
            students: mapped,
          });
          setStudents(mapped);
          
        }
      } catch (e: any) {
        setError(e.message || 'Fetch failed');
      } finally {
        setLoading(false);
        clearTimeout(timeout);
      }
    };
    fetchData();
    return () => clearTimeout(timeout);
  }, [goalId, isFaculty]);

  const doneCount = students.filter(s => s.status >= 50).length;
  const totalCount = students.length;

  

  const handleEdit = (i: number) => {
    setEditIndex(i);
    setTempComments(students[i].comments);
  };
  const handleCancel = () => {
    setEditIndex(null);
    setTempComments('');
  };
  const handleSave = async () => {
    if (editIndex === null) return;
  
    const updated = [...students];
    updated[editIndex].comments = tempComments;
  
    setStudents(updated);
    setData(prev => prev ? { ...prev, students: updated } : prev);
    setEditIndex(null);
    setTempComments('');
  
    const done = updated.filter((s: Student) => s.status >= 50).length;
  
    if (isFaculty) {
      try {
        await postFacultyStudentProgressData(
          {
            Progress__c: `${done}/${updated.length}`,
            Comments__c: updated[editIndex].comments || '',
          },
          goalId!
        );
      } catch (error) {
        console.error('Failed to update progress data on save', error);
      }
    }
  };  

  const handleStatusChange = async (index: number, status: number) => {
    const updated = [...students];
    updated[index].status = status;
    updated[index].isManuallyUpdated = true;
    setStudents(updated);
    const done = updated.filter(s => s.status >= 50).length;
    setData(prev => prev ? {
      ...prev,
      studentsOnTrack: { done, total: updated.length },
      completionPercentage: Math.round((done / updated.length) * 100),
      students: updated,
    } : prev);

    if (isFaculty) {
      try {
        await postFacultyStudentProgressData({
          Progress__c: `${done}/${updated.length}`,
          Comments__c: updated[index].comments || '',
        }, goalId!);
      } catch {
        console.error('Update failed');
      }
    }
  };

  if (loading) return <KenLoader />;
  if (error) return <Typography color="error" align="center">{error}</Typography>;
  if (!data) {
    return showNoDataMessage
      ? <Typography align="center">No data available</Typography>
      : <KenLoader />;
  }

  return (
    <Box
      sx={{
        marginTop: 1,
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: 2,
        width: '100%',
        height: '93%',
      
      }}
    >
      <Box sx={{ padding: '2rem', maxWidth: '900px' }}>
        <Typography variant="h4" gutterBottom textAlign="left">
          {data.title}
        </Typography>

        {/* Top summary and global range slider */}
        <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
  <Paper sx={{ flex: 1, p: 2, backgroundColor: '#e6ecff' }}>
    <Typography variant="h4" fontWeight="500">
      {doneCount}/{totalCount}
    </Typography>
    <Typography color="text.secondary" mt={2}>
      Students on track
    </Typography>
  </Paper>

  <Paper sx={{ flex: 1, p: 2, backgroundColor: '#e6ecff' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '40px', gap: 1 }}>
      <CircularProgress
        variant="determinate"
        value={isFaculty ? data.completionPercentage : (data.currentValue ?? data.completionPercentage)}
        size={40}
        thickness={4}
        sx={{ color: '#0d47a1' }}
      />
      <Typography
        color="#0d47a1"
        fontWeight="500"
        fontSize="18px"
        mt="8px"
      >
        {isFaculty ? data.completionPercentage : (data.currentValue ?? data.completionPercentage)}%
      </Typography>
    </Box>
    <Typography color="text.secondary" >Completion percentage</Typography>
  </Paper>
</Box>

<div style={{ maxHeight: '52vh', overflowY: 'auto', width: '72vw' }}>
        {/* Table */}
        {isFaculty ? (
         <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #ddd', textAlign: 'left' }}>
                <th style={{ padding: '8px', fontWeight: '500' }}>Roll Number</th>
                <th style={{ padding: '8px', fontWeight: '500' }}>Student Name</th>
                <th style={{ padding: '8px', width: '25%', fontWeight: '500' }}>Status</th>
                <th style={{ padding: '8px', width: '25%', fontWeight: '500' }}>Comments</th>
                <th style={{ padding: '8px', fontWeight: '500' }}>Edit</th>
              </tr>
            </thead>
            <tbody>
              {students.map((student, index) => (
                <tr
                  key={student.id}
                  style={{
                    borderBottom: '1px solid #ddd',
                    backgroundColor: index === editIndex ? '#f0f4ff' : 'transparent',
                  }}
                >
                  <td style={{ padding: '8px', width: '25%', textAlign: 'left' }}>{student.id}</td>
                  <td style={{ padding: '8px', width: '25%', textAlign: 'left' }}>{student.name}</td>
                  <td style={{ padding: '8px', width: '25%', textAlign: 'left' }}>
                 {student.status}
                  </td>
                  <td style={{ padding: '8px', width: '25%', textAlign: 'left' }}>
                    {editIndex === index ? (
                      <input
                        type="text"
                        value={tempComments}
                        onChange={(e) => setTempComments(e.target.value)}
                        style={{
                          width: '100%',
                          padding: 6,
                          borderRadius: 4,
                          border: '1px solid #ccc',
                        }}
                      />
                    ) : (
                      student.comments
                    )}
                  </td>
                  <td style={{ padding: '8px', width: '5%' }}>
                    {editIndex === index ? (
                      <>
                        <IconButton aria-label="Save" size="small" onClick={handleSave}>
                          <CheckIcon />
                        </IconButton>
                        <IconButton aria-label="Cancel" size="small" onClick={handleCancel}>
                          <CloseIcon />
                        </IconButton>
                      </>
                    ) : (
                      <IconButton
                        aria-label="Edit"
                        size="small"
                        onClick={() => handleEdit(index)}
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #ddd', textAlign: 'left' }}>
              <th style={{ padding: '8px', fontWeight: '500' }}>Sub-goals</th>
              <th style={{ padding: '8px', fontWeight: '500' }}>Status</th>
              <th style={{ padding: '8px', fontWeight: '500' }}>Comments</th>
              <th style={{ padding: '8px', fontWeight: '500' }}>Edit</th>
            </tr>
          </thead>
          <tbody>
            {students.map((student, index) => (
              <tr key={student.id} style={{ borderBottom: '1px solid #ddd' }}>
                <td style={{ padding: '8px', textAlign: 'left' }}>{student.name}</td>
                <td style={{ padding: '8px', textAlign: 'left' }}>{student.status}</td>
                <td style={{ padding: '8px', textAlign: 'left' }}>
                  {editIndex === index ? (
                    <input
                      type="text"
                      value={tempComments}
                      onChange={(e) => setTempComments(e.target.value)}
                      style={{
                        width: '100%',
                        padding: 6,
                        borderRadius: 4,
                        border: '1px solid #ccc',
                      }}
                    />
                  ) : (
                    student.comments
                  )}
                </td>
                <td style={{ padding: '8px', width: '5%' }}>
                  {editIndex === index ? (
                    <>
                      <IconButton aria-label="Save" size="small" onClick={handleSave}>
                        <CheckIcon />
                      </IconButton>
                      <IconButton aria-label="Cancel" size="small" onClick={handleCancel}>
                        <CloseIcon />
                      </IconButton>
                    </>
                  ) : (
                    <IconButton
                      aria-label="Edit"
                      size="small"
                      onClick={() => handleEdit(index)}
                    >
                      <EditIcon />
                    </IconButton>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        )}
        </div>
      </Box>
    </Box>
  );
};

export default EditableTable;
